import {
  ChangeEvent,
  DragEvent,
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import toast from 'react-hot-toast';

import { Icon } from '@/core/components/Icons';

import { AI_ANALYSIS_FILE_ACCEPT, validateAiAnalysisFiles } from '../../utils/aiAnalysisFiles';
import {
  AttachButton,
  FileChip,
  FileChipName,
  FileChips,
  HiddenFileInput,
  InputFieldWrapper,
  InputInner,
  InputRow,
  InputWrapper,
  RemoveFileButton,
  StyledButton,
  TextInput,
} from './Input.styles';
import { InputProps } from './Input.types';

export const Input = ({
  onSend,
  onStop,
  isGenerating = false,
  placeholder = 'Type your message...',
  canAttachFiles = false,
  isCentered = false,
}: InputProps) => {
  const [value, setValue] = useState('');
  const [files, setFiles] = useState<Array<File>>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const canSend = !isGenerating && (Boolean(value.trim() || files.length) || canAttachFiles);
  const canDropFiles = canAttachFiles && !isGenerating;

  useEffect(() => {
    const textarea = textareaRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [value]);

  const addFiles = useCallback(
    (incoming: Array<File>) => {
      if (!incoming.length) {
        return;
      }

      const { files: nextFiles, error } = validateAiAnalysisFiles(files, incoming);

      if (error) {
        toast.error(error);
      } else {
        setFiles(nextFiles);
      }
    },
    [files]
  );

  const sendMessage = () => {
    if (!canSend) {
      return;
    }

    onSend?.(value.trim(), files);
    setValue('');
    setFiles([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(event.target.files ?? []));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canDropFiles) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!canDropFiles) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!canDropFiles) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    addFiles(Array.from(event.dataTransfer.files ?? []));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <InputWrapper
      $isCentered={isCentered}
      onSubmit={handleSubmit}
    >
      <InputInner>
        {canAttachFiles && files.length > 0 ? (
          <FileChips>
            {files.map((file, index) => (
              <FileChip key={`${file.name}-${file.size}-${index}`}>
                <FileChipName title={file.name}>{file.name}</FileChipName>
                <RemoveFileButton
                  type="button"
                  onClick={() => removeFile(index)}
                  aria-label={`Remove ${file.name}`}
                  disabled={isGenerating}
                >
                  <Icon
                    name="Close"
                    size={10}
                  />
                </RemoveFileButton>
              </FileChip>
            ))}
          </FileChips>
        ) : null}
        <InputRow>
          <InputFieldWrapper
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <TextInput
              ref={textareaRef}
              value={value}
              onChange={event => setValue(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              aria-label="Chat message"
              disabled={isGenerating}
              rows={1}
              $hasAttach={canAttachFiles}
              $isDragOver={isDragOver}
            />
            {canAttachFiles ? (
              <>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept={AI_ANALYSIS_FILE_ACCEPT}
                  multiple
                  onChange={handleFileChange}
                />
                <AttachButton
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isGenerating}
                  aria-label="Attach files"
                >
                  <Icon
                    name="Paperclip"
                    size={20}
                  />
                </AttachButton>
              </>
            ) : null}
            {isGenerating ? (
              <StyledButton
                type="button"
                onClick={onStop}
              >
                <Icon
                  name="Stop"
                  size={20}
                />
              </StyledButton>
            ) : (
              <StyledButton
                type="submit"
                disabled={!canSend}
              >
                <Icon
                  name="ArrowUp"
                  size={10}
                />
              </StyledButton>
            )}
          </InputFieldWrapper>
        </InputRow>
      </InputInner>
    </InputWrapper>
  );
};
